import useOptimisticOrderCustomFields from '@/api/mutations/custom-field-groups/optimistic/useOptimisticOrderCustomFields';
import useArchiveCustomField from '@/api/mutations/custom-field-groups/useArchiveCustomField';
import meKeys from '@/api/queries/me/keyFactory';
import { CustomField, CustomFieldGroup } from '@/gql/graphql';
import CreateEditCustomFieldSidebar from '@/ui/components/customFieldGroupControl/CreateEditCustomFieldSidebar';
import type { UniqueIdentifier } from '@dnd-kit/core';
import {
  Alert,
  Button,
  Card,
  ContextMenu,
  EmptyState,
  Modal,
} from '@payaca/ambrosia';

import { DragHandle, SortableList } from '@payaca/dnd';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, ReactNode, useReducer, useState } from 'react';

type ModalState = {
  activeModal:
    | 'createCustomField'
    | 'editCustomField'
    | 'archiveCustomField'
    | null;
  customFieldId?: CustomField['id'];
};

type ModalAction =
  | {
      type: 'createCustomField';
    }
  | {
      type: 'editCustomField';
      payload: {
        customFieldId: CustomField['id'];
      };
    }
  | {
      type: 'archiveCustomField';
      payload: {
        customFieldId: CustomField['id'];
      };
    }
  | {
      type: 'closeModal';
    };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'createCustomField':
      return { activeModal: 'createCustomField' };
    case 'editCustomField':
      return {
        activeModal: 'editCustomField',
        customFieldId: action.payload.customFieldId,
      };
    case 'archiveCustomField':
      return {
        activeModal: 'archiveCustomField',
        customFieldId: action.payload.customFieldId,
      };
    case 'closeModal':
      return { activeModal: null };
    default:
      return state;
  }
};

export const CustomFieldGroupControl: FC<{
  customFieldGroup: CustomFieldGroup;
  heading?: string;
  subHeading?: string;
  headerAction?: ReactNode;
}> = ({ customFieldGroup, heading, subHeading, headerAction }) => {
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    activeModal: null,
  });

  const { mutate: orderCustomFields } = useOptimisticOrderCustomFields();

  const activeFields = customFieldGroup.customFields.filter(
    (x) => !x.archivedAt
  );

  const sortableItems = activeFields;

  const handleDragEnd = (
    _activeItem: (typeof sortableItems)[0],
    _destinationIndex: number,
    items: typeof sortableItems
  ) => {
    const orderedCustomFieldIds = items.map((item) => item.id);
    orderCustomFields({
      groupId: customFieldGroup.id,
      orderedCustomFieldIds,
    });
  };

  return (
    <div>
      <Card>
        <Card.Body>
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3>{heading || 'Custom Fieldsets'}</h3>
              {subHeading && (
                <p className="supporting-body text-gray-500">{subHeading}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {headerAction}
              <Button
                size="sm"
                variant="white"
                onClick={() =>
                  modalDispatch({
                    type: 'createCustomField',
                  })
                }
              >
                Add custom fieldset
              </Button>
            </div>
          </div>

          {activeFields.length === 0 ? (
            <EmptyState iconName="cursor-box.3" text="No Custom Fields yet" />
          ) : (
            <SortableList
              id={`custom-fields-${customFieldGroup.id}`}
              items={sortableItems}
              onDragEnd={handleDragEnd}
              className="grid gap-2"
              renderItem={(field) => (
                <CustomFieldRow
                  field={field}
                  onEdit={() =>
                    modalDispatch({
                      type: 'editCustomField',
                      payload: { customFieldId: field.id },
                    })
                  }
                  onDelete={() =>
                    modalDispatch({
                      type: 'archiveCustomField',
                      payload: { customFieldId: field.id },
                    })
                  }
                  id={field.id}
                />
              )}
            />
          )}
        </Card.Body>
      </Card>

      <CreateEditCustomFieldSidebar
        isOpen={
          modalState.activeModal === 'createCustomField' ||
          modalState.activeModal === 'editCustomField'
        }
        customFieldId={modalState.customFieldId}
        onClose={() => modalDispatch({ type: 'closeModal' })}
        customFieldGroupRole={customFieldGroup.role}
      />

      <ArchiveCustomFieldModal
        isOpen={modalState.activeModal === 'archiveCustomField'}
        onClose={() => modalDispatch({ type: 'closeModal' })}
        customFieldId={modalState.customFieldId}
      />
    </div>
  );
};

type CustomFieldRowProps = {
  field: CustomFieldGroup['customFields'][0];
  onEdit: () => void;
  onDelete: () => void;
  id: UniqueIdentifier;
};

const CustomFieldRow: FC<CustomFieldRowProps> = ({
  field,
  onEdit,
  onDelete,
  id,
}) => {
  return (
    <Card
      className="cursor-pointer hover:bg-gray-50"
      sizeVariant="xs"
      onClick={onEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      <Card.Body className="flex w-full items-center gap-3">
        <DragHandle as="div" id={id} label={field.label} />
        <h4 className="min-w-0 flex-1 truncate">{field.label}</h4>
        <ContextMenu
          size="xs"
          variant="white"
          onClick={(e) => e.stopPropagation()}
          menuItems={[
            {
              label: 'Delete',
              onClick: (e) => {
                e.stopPropagation();
                onDelete();
              },
            },
          ]}
        />
      </Card.Body>
    </Card>
  );
};

const ArchiveCustomFieldModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  customFieldId?: CustomField['id'];
}> = ({ isOpen, onClose, customFieldId }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const queryClient = useQueryClient();

  const { mutateAsync: archiveCustomField } = useArchiveCustomField();

  const handleArchive = () => {
    if (!customFieldId) {
      return;
    }

    setIsProcessing(true);

    archiveCustomField({
      id: customFieldId,
    })
      .then(() => {
        void queryClient.invalidateQueries({
          queryKey: meKeys.customFieldGroups(),
        });

        onClose();
      })
      .catch(() => {
        setErrorMessage('An error occurred while deleting this field.');
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={'Delete a Custom Field'}>
      <Modal.Body>
        <p>You will lose any data set against this field.</p>
      </Modal.Body>
      <Modal.Footer>
        {errorMessage && (
          <Alert colour="red" className="mb-4">
            {errorMessage}
          </Alert>
        )}
        <Modal.Footer.Actions>
          <Button
            colour={'red'}
            isProcessing={isProcessing}
            onClick={() => !isProcessing && handleArchive()}
          >
            {'Yes, delete'}
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};
